import React, { useState } from 'react';

import { TextButton } from '@components/Button/variants';
import IconClose from '@components/Images/Rebranding/IconClose';
import IconCopyLink from '@components/Images/Rebranding/IconCopyLink';
import SvgIconFacebook from '@components/Images/Rebranding/IconFacebook';
import SvgIconLinkedIn from '@components/Images/Rebranding/IconLinkedIn';
import IconMail from '@components/Images/Rebranding/IconMail';
import SvgIconX from '@components/Images/Rebranding/IconX';
import { EMAIL } from '@components/pages/NewsletterIndexLanding/NewsletterCard/interfaces';
import useEventTracking from '@hooks/useEventTracking';
import Modal from '@mui/material/Modal';
import { newsletter_landing_path } from '@routes';
import { ClickTarget, ContentType, click } from '@tracking';
import { parameterize } from '@utils/formatting';

interface ShareModalProps {
  author: string;
  description: string;
  id: number;
  name: string;
  onClose: () => void;
}

const ShareModal = ({ author, description, id, name, onClose }: ShareModalProps) => {
  const [isOpen, setIsOpen] = useState(!!name);
  const [isCopied, setIsCopied] = useState(false);
  const { trackEvent } = useEventTracking({
    contentType: ContentType.Newsletter,
    contentId: id,
  });
  const host = window.location.host;

  const shareUrl = `https://${host}/${newsletter_landing_path(name)}`;
  const formattedName = parameterize(name);
  const shortShareUrl = `${host}/${parameterize(name)}`;

  const openPopup = (url: string, label: string) => {
    const width = 600;
    const height = 500;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    trackEvent(
      click({
        clickTarget: ClickTarget.ShareNewsletter,
        clickText: label,
      })
    );

    window.open(
      url,
      '_blank',
      `width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`
    );
  };

  const emailBody = `From The Information\n\n${name}\n\n${description}\n\n`;

  const shareLinks = [
    {
      key: 'twitter',
      icon: <SvgIconX className="h-[21px] w-[21px]" />,
      url: `https://twitter.com/intent/tweet?text=${name}&via=theinformation&url=${shortShareUrl}`,
    },
    {
      key: 'linkedin',
      icon: <SvgIconLinkedIn className="h-[21px] w-[21px]" />,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
    },
    {
      key: 'facebook',
      icon: <SvgIconFacebook className="h-[21px] w-[21px]" />,
      url: `https://www.facebook.com/sharer/sharer.php?u=${shortShareUrl}`,
    },
  ];

  const handleCloseModal = () => {
    onClose();
    setIsOpen(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shortShareUrl);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <Modal
      data-testid="newsletter-share-modal"
      open={isOpen}
      disableScrollLock={true}
      className="fixed bottom-0 left-0 right-0 top-0 flex items-center justify-center"
    >
      <div className="relative mx-8 w-full max-w-[450px] bg-white sm:mx-0 sm:max-w-[820px]">
        <IconClose
          className="absolute right-8 top-8 h-[26px] w-[26px] cursor-pointer"
          strokeWidth={1}
          onClick={handleCloseModal}
        />
        <div className="flex w-full justify-center">
          <div className=" px-32 py-64 sm:max-w-[623px] sm:px-0">
            <div>
              <h3 className="m-0 pb-12 font-suisse-neue text-subheadline-sm">Share {name}</h3>
              <span className="font-suisse-intl text-button-md">
                Spread the word about {name} by {author} with friends and colleagues.
              </span>
            </div>
            <div className="align-center my-24 flex w-full items-center gap-x-16">
              {shareLinks.map(({ key, icon, url }) => (
                <TextButton
                  key={key}
                  as="button"
                  onClick={() => openPopup(url, key)}
                  className="!w-auto"
                >
                  {icon}
                </TextButton>
              ))}
              <TextButton
                as="externalLink"
                className="!w-auto"
                href={`mailto:?subject=The Information: ${name}&body=From The Information%0d%0d${name}%0d%0d${emailBody}%0d%0dhttps://theinformation.com/${formattedName}`}
                onClickCapture={() =>
                  trackEvent(
                    click({
                      clickTarget: ClickTarget.ShareNewsletter,
                      clickText: EMAIL,
                    })
                  )
                }
              >
                <IconMail className="h-[18px] w-[27px] hover:fill-primary-magenta" />
              </TextButton>
            </div>
            <div className="relative max-w-[368px]">
              <input
                value={shortShareUrl}
                readOnly
                className="h-[50px] w-full rounded-none border-2 border-solid border-primary-magenta px-8 py-16 pr-112 font-suisse-intl text-overline-md text-grayscale-gray placeholder-grayscale-gray focus:outline-none disabled:bg-white disabled:text-grayscale-gray disabled:opacity-40"
              />
              <TextButton
                as="button"
                onClick={handleCopyLink}
                className="!absolute right-0 top-0 flex h-[50px] !w-auto items-center gap-4 bg-grayscale-gray font-suisse-screen text-disclosure-sm !text-grayscale-gray sm:w-[50px]"
                onClickCapture={() => {
                  trackEvent(
                    click({ clickTarget: ClickTarget.CopyShareLink, clickText: 'Copy Link' })
                  );
                }}
              >
                <IconCopyLink className="h-[13px] w-[13px] fill-grayscale-gray" />

                <span>{isCopied ? 'Copied' : 'Copy Link'}</span>
              </TextButton>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShareModal;
